import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from 'components/Icon';
import {
  getBadgeLabel,
  SHOW_STATE_AUDIO,
  SHOW_STATE_CLIP,
  SHOW_STATE_LIVE,
  SHOW_STATE_UPCOMING,
} from 'helpers/BadgeHelper';
import {
  formatAiredDate,
  formatDurationInSeconds,
  formatMetadata,
  formatMovieMetadata,
  formatRating as helperFormatRating,
  formatSeasonEpisode,
  formatSeasonEpisodeExtended,
  formatSubRatings,
  getItemBadge as helperGetItemBadge,
} from 'helpers/VideoHelper';
import {
  getAuthorizedResources,
  isContentAuthorized,
  isRatingAuthorized,
} from 'components/AuthClientless/AuthHelper';
import { isMobileBreakpoint } from 'helpers/BreakpointHelper';
import {
  COLLECTION_TYPE_LOCAL_CLIPS,
  formatUpcomingDate,
  getDisplayBrand,
  getMVPDCookie,
  getNetworkLogoUrl,
  getRequiresAuth,
  getSeasonEpisodeCounts,
  isClip,
  isCurrentLiveContent,
  isEpisode,
  isLive,
  isMovie,
  isSpecial,
  isSportsContent,
  isStaticRender,
  isTypeEPGListing,
  isTypeSeries,
  isTypeVideo,
  isUpcomingContent,
  LINK_REL,
  TYPE_EPG_LISTING,
  videoIsLive,
} from 'helpers/ContentHelper';
import { getDynamicImage } from 'helpers/ImageHelper';
import { CONTINUOUS, PLAYLIST } from 'helpers/PlayerHelper';
import NetworkImage from 'components/NetworkImage';
import {
  getUrlParams,
  helperGetLiveUrl,
  helperGetRelativeUrl,
  helperGetSeriesDetailUrl,
  helperGetStaticContentUrl,
  helperGetVideoUrl,
} from 'helpers/RouteHelper';
import tileTheme from 'components/TileLegacy/TileLegacy.module.scss';
import { getAdditionalAuthorizedNetworks, isPreviewPassAvailable } from 'helpers/AuthHelper';

import { getAuthNetworksConfig, getThumbnailBug } from 'helpers/ConfigHelper';
import {
  getRecommendationIdDataForItem,
  handleTealiumButtonAction,
} from '@foxcorp/delta-web-analytics';
import {
  modifyShowsPageTileCTAS as optimizelyModifyShowsTileCTA,
  modifyTileImage as optimizelyModifyTileImage,
  showShowsTileCTAs as optimizelyShowsTileCTAs,
} from './OptimizelyHelper';
import {
  FORMAT_TIME,
  FORMAT_DISTANCE,
  now,
  stringToDate,
  isBefore,
  isBetween,
  differenceInSeconds,
  addSeconds,
} from './DateTimeHelper';

export const TYPE_SERIES = 'Series';

const networkFallbackImages = {
  fox: helperGetStaticContentUrl('images', 'FallBack-FOX-min.jpg'),
  foxnation: helperGetStaticContentUrl('images', 'FallBack-FOXNATION-min.jpg'),
  fs1: helperGetStaticContentUrl('images', 'FallBack-FOX-min.jpg'),
  fs2: helperGetStaticContentUrl('images', 'FallBack-FOX-min.jpg'),
};

const externalRedirectProcessing = videoData => {
  const newVideoData = videoData;
  const { externalRedirectMapping: redirectRules } = window.ExternalConfig;
  if (Array.isArray(redirectRules)) {
    redirectRules.forEach(rule => {
      const testLink = videoData.link || videoData.titleLink;
      if (rule.path === testLink || `${rule.path}/` === testLink) {
        const newLink = rule.destination;
        newVideoData.link = newLink;
        newVideoData.titleLink = newLink;
        newVideoData.subtitleLink = newLink;
        if (!_.startsWith(newLink, '/')) {
          newVideoData.target = '_blank';
          newVideoData.rel = LINK_REL;
        }
      }
    });
  }
  return newVideoData;
};

export const requiresAuthElement = ({ requiresAuth, lockClass, isAuthenticated, viewBox }) => {
  if (!requiresAuth || isAuthenticated) return null;
  return (
    <div className={classNames([tileTheme.lock, lockClass])}>
      <Icon icon="lock" color="white" size="full" viewBox={viewBox} />
    </div>
  );
};

export const titleElement = ({
  title,
  requiresAuth,
  theme,
  isAuthenticated,
  hideLockIcon,
  isHoverStateV2,
  video = {},
}) => {
  if (!title || title.length === 0) return null;
  const { seasonNumber = '', episodeNumber = '', isLiveNow = false } = video;
  const type = _.get(video, '@type', null);
  const displaySeasonAndEpisodeCounts = _.get(video, 'displaySeasonAndEpisodeCounts', null);
  const titleTheme = theme || tileTheme || {};

  const isSeasonEpisode = seasonNumber && episodeNumber;
  const isLiveSeasonEpisode = isLive(type) && displaySeasonAndEpisodeCounts && isSeasonEpisode;

  let element = (
    <span>
      {title}
      {isLiveSeasonEpisode && ` - ${formatSeasonEpisode(seasonNumber, episodeNumber)}`}
    </span>
  );

  if (requiresAuth && !isPreviewPassAvailable()) {
    const lockClasses = [
      titleTheme.lock,
      titleTheme.titleLock,
      isHoverStateV2 && titleTheme.hoverStateV2Lock,
      isLiveNow && tileTheme.isLiveNow,
    ];
    const lockElement =
      !hideLockIcon &&
      requiresAuthElement({
        requiresAuth,
        lockClass: classNames(lockClasses),
        isAuthenticated,
      });

    const titleParts = _.split(title, '-');
    const lastWord = titleParts.splice(-1);

    element = (
      <>
        <span>
          {`${titleParts.join(' ')} ${lastWord}`}
          {isLiveSeasonEpisode && ` - ${formatSeasonEpisode(seasonNumber, episodeNumber)}`}
        </span>
        {lockElement}
      </>
    );
  }
  const titleClasses = classNames([
    tileTheme && tileTheme.titleWrapper,
    theme && theme.titleWrapper,
  ]);
  return <div className={titleClasses}>{element}</div>;
};

export const getHomeSubtitleText = (video, isHoverStateV2) => {
  const {
    '@type': type,
    durationInSeconds,
    episodeNumber,
    fullEpisodeCount: episodeCount,
    name,
    releaseYear,
    seriesType,
    seasonCount,
    seasonNumber,
    sportTag,
    startDate,
    videoType,
  } = video;
  const displaySeasonAndEpisodeCounts = _.get(video, 'displaySeasonAndEpisodeCounts', true);
  const currentSeason = _.get(video, 'currentSeason') || _.get(video, 'oldestEpisode.seasonNumber');
  const currentSeasonNumber = currentSeason ? currentSeason.seasonNumber : null;
  const seasonEpisodeFormats = {
    short: displaySeasonAndEpisodeCounts
      ? formatSeasonEpisode(seasonNumber, episodeNumber, isHoverStateV2)
      : '',
    regular: displaySeasonAndEpisodeCounts
      ? getSeasonEpisodeCounts({ currentSeason, seasonCount, episodeCount, useComma: true })
      : '',
    extended: displaySeasonAndEpisodeCounts
      ? formatSeasonEpisodeExtended(seasonCount, currentSeasonNumber, episodeCount)
      : '',
  };

  const videoIsClip = isClip(videoType);
  const videoIsSeries = isTypeSeries(type);

  if (isLive(type) && startDate) {
    const videoIsUpcoming = isBefore(now(), stringToDate(startDate));
    if (videoIsUpcoming) return formatUpcomingDate(startDate);
    return formatMovieMetadata({
      releaseYear: formatAiredDate(startDate, '', FORMAT_TIME),
      durationInSeconds,
      continuesStreamCheck: true,
    });
  }

  if (videoIsClip) {
    return name;
  }

  if (isSpecial(seriesType)) {
    return null;
  }

  if (isMovie(videoType)) {
    return _.toString(releaseYear);
  }

  if (videoIsSeries) {
    return seasonEpisodeFormats.extended;
  }

  if (isEpisode(videoType) || videoIsClip) {
    if (isHoverStateV2 && seasonEpisodeFormats.short)
      return `${seasonEpisodeFormats.short}- ${name}`;
    return `${seasonEpisodeFormats.short}${name}`;
  }

  if (isSportsContent(video)) {
    return sportTag || null;
  }

  return null;
};

export const subtitleElement = (subtitle, date, shouldHideVideoSubtitle) => {
  if (!subtitle || subtitle.length === 0) return null;
  const subtitleParts = subtitle.split(' ');
  const lastWord =
    subtitleParts.length > 1 && !shouldHideVideoSubtitle ? subtitleParts.splice(-1) : null;
  const dot = shouldHideVideoSubtitle ? '' : '·';
  const subtitleDate = date && (
    <span>
      {dot} {date}
    </span>
  );
  const subtitleTags = (
    <span>
      {!shouldHideVideoSubtitle && subtitleParts.join(' ')}
      <div className={tileTheme.subtitleLastWord}>
        {lastWord} {subtitleDate}
      </div>
    </span>
  );
  return (
    <div className={tileTheme.subtitleWrapper}>
      <div className={tileTheme.subtitleText}>{subtitleTags}</div>
    </div>
  );
};

export const isAuthorized = ({
  prioritizedNetwork,
  preauthorizedResources,
  isAuthenticated,
  videoType,
  contentRating,
  requiresAuth,
}) => {
  const mvpdCookie = getMVPDCookie();
  const userRating = _.get(mvpdCookie, 'userRating');
  if (isAuthenticated && videoType !== 'clip') {
    const authNetworksConfig = getAuthNetworksConfig();
    const additionalAuthorizedNetworks = getAdditionalAuthorizedNetworks();
    const authorizedNetworks = getAuthorizedResources(
      preauthorizedResources,
      additionalAuthorizedNetworks,
    );
    const isNetworkAuthorized = requiresAuth
      ? isContentAuthorized(authorizedNetworks, [prioritizedNetwork], authNetworksConfig)
      : true;
    const isVideoAuthorized = isRatingAuthorized(contentRating, userRating);
    return isNetworkAuthorized && isVideoAuthorized;
  }
  return true;
};

export const getVideoObjectImages = ({ images, defaultImage }) => {
  if (!images) return null;
  const tileImageType = optimizelyModifyTileImage() || defaultImage;
  const image =
    _.get(images, tileImageType, null) ||
    _.get(images, 'seriesList', null) ||
    _.get(images, 'showList', null) ||
    _.get(images, 'videoList', null) ||
    _.get(images, 'seriesDetail', null) ||
    _.get(images, 'seriesStill', null) ||
    _.get(images, 'still', null) ||
    images;
  return image;
};

export const getVideoObjectImage = ({ breakpoint, context, image, network }) => {
  const fallbackImage = _.get(networkFallbackImages, network, null);
  if (!image) return fallbackImage;
  let imageUrl = image.raw;
  if (breakpoint && context) {
    imageUrl = getDynamicImage({ breakpoint, context, url: imageUrl });
  }
  return imageUrl || fallbackImage;
};

export const getItemBadge = (item, isLiveItem, isHoverStateV2, isPosters, isLocalClips) => {
  if (!item) return null;
  const { audioOnly, contentFlags, startDate, durationInSeconds, '@type': type, videoType } = item;
  if (isLiveItem) return getBadgeLabel(SHOW_STATE_LIVE);
  if (isLocalClips || videoType === 'clip') return getBadgeLabel(SHOW_STATE_CLIP);

  const isAudio = audioOnly || _.includes(contentFlags, 'AUDIO ONLY');
  const isContinueListening = isAudio
    ? _.includes(contentFlags, 'Continue Watching') ||
      _.includes(contentFlags, 'Continue Listening')
    : false;
  if (isContinueListening) return getBadgeLabel('Continue Listening');
  if (isAudio) return getBadgeLabel(SHOW_STATE_AUDIO);

  const originalDate = item.startDate;
  const isEpgListing = type === TYPE_EPG_LISTING;

  const startsAt = stringToDate(originalDate);
  const endsAt = addSeconds(stringToDate(startDate), durationInSeconds);

  if (isEpgListing && startsAt && endsAt) {
    const videoIsUpcoming = isBefore(now(), startsAt);
    const videoIsLive = isBetween(now(), startsAt, endsAt);
    if (videoIsUpcoming) return getBadgeLabel(SHOW_STATE_UPCOMING);
    if (videoIsLive) return getBadgeLabel(SHOW_STATE_LIVE);
    return null;
  }

  const itemBadge = helperGetItemBadge(item, isHoverStateV2, isPosters);
  return getBadgeLabel(itemBadge);
};

const getPrimetimeScheduleItemBadge = item => {
  if (!item) return null;

  const { startDate, durationInSeconds, '@type': type } = item;
  const isEpgListing = type === TYPE_EPG_LISTING;
  const currentDate = now();
  const starts = startDate ? stringToDate(startDate) : null;
  const ends = starts ? addSeconds(starts, durationInSeconds) : null;
  const isLive = starts && ends ? isBetween(currentDate, starts, ends) : false;

  if (isEpgListing && starts && ends) {
    if (isLive) return getBadgeLabel(SHOW_STATE_AUDIO);
  }

  return helperGetItemBadge(item);
};
export const isTileFaded = (video, prioritizedNetwork) => {
  const { preauthorizedResources, videoType, contentRating, isAuthenticated } = video;

  const requiresAuth = getRequiresAuth({ video });
  const isVideoAuthorized = isAuthorized({
    preauthorizedResources,
    videoType,
    prioritizedNetwork,
    contentRating,
    isAuthenticated,
    requiresAuth,
  });

  return !isVideoAuthorized;
};

const formatRating = (contentRating, subRatings) => {
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  return rating || subRating ? `${rating} ${subRating}` : null;
};

const getPlaylistHash = playerScreenUrl => {
  if (!_.includes(playerScreenUrl, 'playlist')) return null;
  const urlParams = getUrlParams(playerScreenUrl);
  const playlistId = _.get(urlParams, 'playlist');
  return `${PLAYLIST}=${playlistId}`;
};

export const homeVideoProcessor = ({
  account,
  breakpoint,
  collectionInfo,
  isCollectionCategoryList = false,
  isHoverStateV2,
  isLocalClips,
  isPosters,
  item,
}) => {
  if (!item) return null;
  const {
    '@id': id,
    '@type': type,
    bookmarkSeconds,
    callSign,
    categoryType,
    contentLabel,
    contentRating,
    description,
    descriptionLines,
    detailUrl,
    durationInSeconds,
    endDate,
    episodeNumber,
    fullEpisodeCount,
    guid,
    id: videoId,
    images,
    isAuthenticated,
    isLiveNow,
    isReplay,
    isSeriesDetailAvailable,
    isSeriesPromo,
    livePlayerScreenUrl,
    name,
    originalAirDate,
    percentWatched,
    playerScreenUrl,
    releaseYear,
    seasonNumber,
    seriesName,
    seriesScreenUrl,
    seriesType,
    showCode,
    startDate,
    subRatings,
    theme,
    trackingData,
    uID,
    videoType,
  } = item;
  const { global_watchButton: watchButtonText } = window.Language;
  const requiresAuth = getRequiresAuth({ video: item, isLive: !!livePlayerScreenUrl });
  const displayBrand = getDisplayBrand(item);
  const imageObj = getVideoObjectImages({ images, defaultImage: 'seriesList' });
  const hoverImageObj = getVideoObjectImages({ images, defaultImage: 'videoList' });
  const imageAltText = _.get(imageObj, 'altText', '');
  const fadeTile = isTileFaded(item, displayBrand);
  const badge = getItemBadge(item, false, isHoverStateV2, isPosters, isLocalClips);
  const airDate = formatAiredDate(originalAirDate);
  const formattedRating = formatRating(contentRating, subRatings);
  const seasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const videoIsMovieType = isMovie(videoType);
  const videoIsSpecialType = isSpecial(seriesType);
  const videoIsTypeLive = isLive(type);
  const showLiveProgressBar = videoIsTypeLive && !isStaticRender();
  const subtitle = getHomeSubtitleText(item, isHoverStateV2);
  const isSportingEvent = isSportsContent(item);
  const affiliateLogoFallback = helperGetStaticContentUrl('images', 'fox-network-logo.png');
  const affiliateLogo = images?.affiliateLogoAlt?.raw ?? affiliateLogoFallback;
  let image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  let hoverImage = getVideoObjectImage({ image: hoverImageObj, network: displayBrand });
  let metadata = formatMetadata([airDate, formattedRating]);
  let titleLink = helperGetRelativeUrl({ url: seriesScreenUrl });
  let preventToggle = isSportingEvent;
  let playButton = true;
  const ctaData = [];
  const autoPlayVideo = _.get(item, 'autoPlayVideo.default.url');
  const playlistHash = getPlaylistHash(playerScreenUrl);
  let link = helperGetRelativeUrl({
    hash: playlistHash,
    url: playerScreenUrl || seriesScreenUrl || detailUrl,
  });
  const isUpcoming = isUpcomingContent(item);
  if (videoIsTypeLive) {
    const secUntilStart = differenceInSeconds(stringToDate(startDate), now());
    const videoStartsWithinFiveMinutes = isUpcoming && secUntilStart && secUntilStart <= 300;
    const videoIsCurrentlyLive = isCurrentLiveContent(item);
    const linkToLive =
      livePlayerScreenUrl && (videoStartsWithinFiveMinutes || videoIsCurrentlyLive);
    if (linkToLive) {
      link = helperGetLiveUrl({ callSign, videoId });
      if (isSportingEvent) titleLink = link;
    } else if (isSportingEvent) {
      link = null;
      titleLink = null;
    } else if (seriesScreenUrl) {
      link = helperGetRelativeUrl({ url: seriesScreenUrl });
    } else {
      link = null;
    }
  }
  let subtitleLink = link;

  if (videoIsMovieType) {
    metadata = formatMovieMetadata({
      releaseYear,
      contentRating,
      subRatings,
      durationInSeconds,
    });
  }

  if (
    isSeriesPromo &&
    (isEpisode(videoType) || isClip(videoType)) &&
    !isSportsContent(item) &&
    playerScreenUrl &&
    !livePlayerScreenUrl
  ) {
    const showLock = requiresAuth && !isAuthenticated;
    const showResume =
      typeof percentWatched === 'number' && percentWatched > 0 && percentWatched <= 90;
    const ctaText = showResume ? 'Resume' : watchButtonText;
    ctaData.push({
      icon: showLock ? 'lock' : 'play-button',
      viewBox: showLock ? '-7 -1 30 30' : null,
      text: `${ctaText} ${seasonEpisode}`,
      url: link,
    });
  }
  let seriesSeasonEpisode = '';
  let seriesPlayerScreenUrl = null;
  if (isTypeSeries(type)) {
    titleLink = helperGetSeriesDetailUrl(showCode);
    const oldestEpisode = _.get(item, 'oldestEpisode');
    seriesPlayerScreenUrl = _.get(oldestEpisode, 'playerScreenUrl');
    const seriesRating = _.get(oldestEpisode, 'contentRating');
    const seriesSubRating = _.get(oldestEpisode, 'subRatings');
    const seriesFormattedRating = formatRating(seriesRating, seriesSubRating);
    metadata = formatMetadata([seriesFormattedRating]);
    subtitleLink = titleLink;
    preventToggle = true;
    if (seriesPlayerScreenUrl) {
      link = helperGetRelativeUrl({ url: seriesPlayerScreenUrl });
      const seasonNumberOldestEpisode = _.get(oldestEpisode, 'seasonNumber');
      const episodeNumberOldestEpisode = _.get(oldestEpisode, 'episodeNumber');
      seriesSeasonEpisode =
        seasonNumberOldestEpisode && episodeNumberOldestEpisode
          ? formatSeasonEpisode(seasonNumberOldestEpisode, episodeNumberOldestEpisode)
          : null;
    } else {
      link = titleLink;
      playButton = false;
    }
  }

  const lines = descriptionLines === undefined ? 2 : descriptionLines;
  const {
    global_moreInfoButton: moreInfoText,
    global_moreSeriesDeeplinkButton: moreEpisodesText,
  } = window.Language;
  const seeAllText = fullEpisodeCount > 1 ? moreEpisodesText : moreInfoText;
  const hideLockIcon = (!videoIsMovieType && !videoIsSpecialType) || videoIsTypeLive;
  let titleValue = videoIsMovieType ? name : seriesName || name;

  if (isCollectionCategoryList) {
    const seriesSquareFallback = helperGetStaticContentUrl('images', 'series-square-fallback.jpg');
    image = images?.seriesSquare?.raw ?? seriesSquareFallback;
    hoverImage = images?.seriesSquare?.raw ?? seriesSquareFallback;
    titleValue = item?.headline;
  }

  const title = titleElement({
    hideLockIcon,
    isAuthenticated,
    isHoverStateV2,
    requiresAuth,
    theme,
    title: titleValue,
  });
  const recommendationIdData = _.get(trackingData, 'properties.recommendationIdData', '');
  const trackingContentId = _.get(trackingData, 'properties.contentId', null);
  const contentId = trackingContentId || (videoType === 'clip' ? guid : uID);

  const tealiumTileType = (() => {
    if (videoIsTypeLive) return 'live tile';
    if (isTypeVideo(type)) return 'video tile';
    return 'show tile';
  })();
  const videoData = {
    account,
    affiliateLogo,
    badge,
    bookmarkSeconds,
    categoryType,
    contentId,
    contentLabel,
    ctaData,
    ...collectionInfo,
    description,
    descriptionLines: lines,
    durationInSeconds,
    endDate,
    fadeTile,
    hoverImage,
    id,
    image,
    imageAltText,
    isReplay,
    isSeries: isTypeSeries(type),
    isSeriesDetailAvailable,
    link,
    metadata,
    name,
    networkIcon,
    percentWatched,
    playButton,
    preventToggle,
    recommendationIdData,
    requiresAuth,
    seeAllText,
    seriesName,
    seriesPlayerScreenUrl,
    seriesSeasonEpisode,
    showCode,
    showLiveProgressBar,
    startDate,
    tealiumTileType,
    subtitle: subtitleElement(subtitle),
    subtitleLink,
    subtitleName: name,
    title,
    titleLink,
    titleLines: isLiveNow && isMobileBreakpoint(breakpoint) ? 1 : 3,
    toggleEnabled: false,
    trackButtonClick: handleTealiumButtonAction,
    videoType,
  };
  videoData.video = autoPlayVideo;
  return videoData;
};

export const seriesVideoProcessor = ({ item, isLive }) => {
  if (!item) return null;
  const {
    '@id': id,
    '@type': type,
    bookmarkSeconds,
    contentLabel,
    contentRating,
    description,
    displaySeasonAndEpisodeCounts,
    displayUpsellCTA,
    durationInSeconds,
    episodeNumber,
    id: videoId,
    images,
    isAuthenticated,
    isLiveNow,
    isSeriesDetailAvailable,
    name,
    originalAirDate,
    percentWatched,
    playerScreenUrl,
    seasonNumber,
    subRatings,
    theme,
  } = item;
  const requiresAuth = getRequiresAuth({ video: item });
  const displayBrand = getDisplayBrand(item);
  const fadeTile = isTileFaded(item, displayBrand);
  const imageObj = getVideoObjectImages({ images, defaultImage: 'videoList' });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const badge = getItemBadge(item, isLive);
  const airDate = formatAiredDate(originalAirDate);
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;
  const metadata = formatMetadata([airDate, ratingStr]);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  let titleText;
  if (displaySeasonAndEpisodeCounts) {
    const seasonNumFormat = item.seasonNumber ? `S${item.seasonNumber}` : '';
    const episodeNumFormat = item.episodeNumber ? ` E${item.episodeNumber}` : '';
    titleText = `${seasonNumFormat}${episodeNumFormat} ${name}`;
  } else {
    titleText = name;
  }
  const title = titleElement({ title: titleText, requiresAuth, theme, isAuthenticated });
  const liveLink = helperGetLiveUrl({ videoId });
  const subtitleLines = isLive ? 2 : 0;
  const seasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
  const episodeSeasonAndTitle = `${seasonEpisode}${name}`;

  let link = '';
  if (isTypeVideo(type)) {
    link = playerScreenUrl
      ? helperGetRelativeUrl({ url: playerScreenUrl })
      : helperGetVideoUrl(videoId);
  } else if (isTypeEPGListing(type) && isLiveNow) {
    link = helperGetLiveUrl({ videoId });
  } else {
    link = helperGetRelativeUrl({ url: playerScreenUrl });
  }

  const videoData = {
    badge,
    bookmarkSeconds,
    contentLabel,
    displayUpsellCTA,
    durationInSeconds,
    episodeSeasonAndTitle,
    fadeTile,
    id,
    image,
    isLive,
    isSeries: isTypeSeries(type),
    isSeriesDetailAvailable,
    link,
    liveLink,
    metadata,
    networkIcon,
    originalItem: item,
    percentWatched,
    playButton: true,
    subtitle: description,
    subtitleLines,
    title,
    titleText,
    trackButtonClick: handleTealiumButtonAction,
  };

  const { upsellPromoThumbnailBug, upsellPromoThumbnailButton } = window.Language;

  const upsellVideoData = {
    ...videoData,
    title: titleElement({ title: titleText, requiresAuth: false, theme, isAuthenticated }),
    playButton: false,
    networkIcon: null,
    upsellDogEarImage: getThumbnailBug(),
    upsellDogEarText: upsellPromoThumbnailBug,
    upsellButtonText: upsellPromoThumbnailButton,
    upsellButtonIcon: <NetworkImage />,
    episodeSeasonAndTitle,
  };
  return displayUpsellCTA ? upsellVideoData : videoData;
};

export const clipsVideoProcessor = ({ item }) => {
  if (!item) return null;

  const {
    '@id': id,
    '@type': type,
    available,
    bookmarkSeconds,
    contentLabel,
    datePublished,
    durationInSeconds,
    images,
    isSeriesDetailAvailable,
    name,
    originalAirDate,
    percentWatched,
    playerScreenUrl,
  } = item;
  const displayBrand = getDisplayBrand(item);

  const imageObj = getVideoObjectImages({ images, defaultImage: 'videoList' });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });

  const badge = getItemBadge(item);
  const airDate = formatAiredDate(
    originalAirDate || datePublished || available,
    'Published ',
    'MM-DD-YY',
  );
  const duration = formatDurationInSeconds(durationInSeconds);
  const metadata = formatMetadata([airDate, duration]);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const recommendationIdData = getRecommendationIdDataForItem(item);

  const videoData = {
    badge,
    bookmarkSeconds,
    contentLabel,
    durationInSeconds,
    id,
    image,
    isSeries: isTypeSeries(type),
    isSeriesDetailAvailable,
    link: helperGetRelativeUrl({ url: playerScreenUrl }),
    metadata,
    networkIcon,
    percentWatched,
    playButton: true,
    recommendationIdData,
    title: name,
    titleLines: 2,
    titleLink: helperGetRelativeUrl({ url: playerScreenUrl }),
    trackButtonClick: handleTealiumButtonAction,
  };

  return videoData;
};

export const prePremiereVideoProcessor = ({ item }) => {
  if (!item) return null;
  const videoData = seriesVideoProcessor({ item });
  if (!videoData) return null;

  const { name } = item;
  const requiresAuth = getRequiresAuth({ video: item });
  videoData.title = titleElement({ title: name, requiresAuth });
  return videoData;
};

export const watchVideoProcessor = ({
  collectionType = '',
  item,
  showSeriesName = false,
  theme,
}) => {
  if (!item) return null;

  const {
    '@id': id,
    '@type': type,
    available,
    bookmarkSeconds,
    contentLabel,
    contentRating,
    datePublished,
    description,
    displaySeasonAndEpisodeCounts,
    durationInSeconds,
    episodeNumber,
    images,
    isAuthenticated,
    isSeriesDetailAvailable,
    name,
    originalAirDate,
    percentWatched,
    playerScreenUrl,
    seasonNumber,
    seriesName,
    seriesScreenUrl,
    subRatings,
  } = item;
  const requiresAuth = getRequiresAuth({ video: item });
  const displayBrand = getDisplayBrand(item);
  const imageObj = getVideoObjectImages({ images, defaultImage: 'videoList' });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const seasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
  let title = displaySeasonAndEpisodeCounts ? `${seasonEpisode} ${name}` : name;
  title = showSeriesName ? `${seriesName} - ${title}` : title;
  const selectedDate = originalAirDate || datePublished || available;
  const airDate = formatAiredDate(selectedDate, '', 'MM-DD-YY');
  const duration = formatDurationInSeconds(durationInSeconds);
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;
  const badge = getItemBadge(item);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const fadeTile = isTileFaded(item, displayBrand);
  const timeSinceAired = formatAiredDate(originalAirDate, '', FORMAT_DISTANCE);
  let metadata = [];
  if (airDate) metadata.push(airDate);
  if (ratingStr) metadata.push(ratingStr);
  if (duration) metadata.push(duration);
  metadata = formatMetadata(metadata);

  if (collectionType === COLLECTION_TYPE_LOCAL_CLIPS) {
    metadata = `${item?.headline} · ${timeSinceAired}`;
  }

  const recommendationIdData = getRecommendationIdDataForItem(item);
  const videoData = {
    badge,
    id,
    image,
    bookmarkSeconds,
    contentLabel,
    isSeries: isTypeSeries(type),
    description,
    descriptionLines: 2,
    durationInSeconds,
    fadeTile,
    isSeriesDetailAvailable,
    link: helperGetRelativeUrl({ url: playerScreenUrl }),
    metadata,
    networkIcon,
    originalItem: item,
    percentWatched,
    playButton: true,
    recommendationIdData,
    seriesScreenUrl,
    subtitleText: metadata,
    tileType: 'tile-text-center',
    title: titleElement({ title, requiresAuth, isAuthenticated, theme }),
    titleLines: 4,
    titleLink: helperGetRelativeUrl({ url: playerScreenUrl }),
    titleText: title,
    toggleEnabled: true,
    trackButtonClick: handleTealiumButtonAction,
  };
  return videoData;
};

export const watchListVideoProcessor = ({ item }) => {
  if (!item) return null;

  const { badge_nowPlaying: badgeNowPlaying } = window.Language;

  const {
    '@id': id,
    name,
    images,
    description,
    seasonNumber,
    episodeNumber,
    displaySeasonAndEpisodeCounts,
    durationInSeconds,
    originalAirDate,
    playerScreenUrl,
    seriesScreenUrl,
    available,
    datePublished,
    contentRating,
    network,
    subRatings,
    percentWatched,
    isAuthenticated,
    preauthorizedResources,
    videoType,
  } = item;

  const requiresAuth = getRequiresAuth({ video: item });
  const isVideoAuthorized = isAuthorized({
    preauthorizedResources,
    videoType,
    prioritizedNetwork: network,
    contentRating,
    isAuthenticated,
    requiresAuth,
  });
  const displayBrand = getDisplayBrand(item);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const seasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
  const title = `${displaySeasonAndEpisodeCounts ? seasonEpisode : ''} ${name}`;
  const selectedDate = originalAirDate || datePublished || available;
  const airDate = formatAiredDate(selectedDate, '', 'MM-DD-YY');
  const duration = formatDurationInSeconds(durationInSeconds);
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;
  const badge = getItemBadge(item);
  const networkIcon = getNetworkLogoUrl(displayBrand);

  let metadata = [];
  if (airDate) metadata.push(airDate);
  if (ratingStr) metadata.push(ratingStr);
  if (duration) metadata.push(duration);
  metadata = formatMetadata(metadata);

  const playButton = badgeNowPlaying !== badge;
  const videoData = {
    id,
    image,
    toggleEnabled: true,
    description,
    descriptionLines: 2,
    tileType: 'tile-text-center',
    subtitle: metadata,
    title: titleElement({ title, requiresAuth, isAuthenticated }),
    playButton,
    link: helperGetRelativeUrl({ url: playerScreenUrl }),
    titleLink: helperGetRelativeUrl({ url: playerScreenUrl }),
    seriesScreenUrl,
    badge,
    networkIcon,
    percentWatched,
    requiresAuth: isAuthenticated ? false : requiresAuth,
    isAuthorized: isVideoAuthorized,
    originalItem: item,
  };
  return videoData;
};

export const searchListVideoProcessor = ({ item }) => {
  if (!item) return null;
  const {
    '@id': id,
    badge,
    name,
    images,
    playerScreenUrl,
    screenUrl,
    seriesScreenUrl,
    detailUrl,
  } = item;

  const { seriesRedirectMap } = window?.ExternalConfig || {};
  const displayBrand = getDisplayBrand(item);
  const recommendationIdData = getRecommendationIdDataForItem(item);
  const imageObj = getVideoObjectImages({ images, defaultImage: 'videoList' });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const url = playerScreenUrl || screenUrl || seriesScreenUrl || detailUrl;

  let linkOverride = null;

  if (item?.seriesType === 'series' && seriesRedirectMap?.[item?.showCode]) {
    linkOverride = seriesRedirectMap?.[item?.showCode];
  }

  if (item?.deeplinkWeb) {
    linkOverride = item.deeplinkWeb;
  }

  return {
    badge,
    id,
    image,
    link: linkOverride || helperGetRelativeUrl({ url }),
    title: name,
    networkIcon,
    originalItem: item,
    playButton: false,
    titleLines: 1,
    titleLink: linkOverride || helperGetRelativeUrl({ url }),
    titleText: name,
    recommendationIdData,
  };
};

export const watchPlaylistVideoProcessor = (playlist, { item }) => {
  const returnValue = watchVideoProcessor({ item });
  const { link, titleLink } = returnValue;
  return {
    ...returnValue,
    link: `${link}#playlist=${playlist}`,
    titleLink: `${titleLink}#playlist=${playlist}`,
  };
};

export const watchListPlaylistVideoProcessor = (playlist, { item }) => {
  const returnValue = watchListVideoProcessor({ item });
  const { link, titleLink } = returnValue;
  return {
    ...returnValue,
    link: `${link}#playlist=${playlist}`,
    titleLink: `${titleLink}#playlist=${playlist}`,
  };
};

export const replayVideoProcessor = ({ item }) => {
  if (!item) return null;

  const { '@id': id, badge, images, name, oldestEpisode, screenUrl } = item;

  const displayBrand = getDisplayBrand(item);
  const recommendationIdData = getRecommendationIdDataForItem(item);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const networkIcon = getNetworkLogoUrl(displayBrand);
  let subtitle;
  const originalAirDate = _.get(oldestEpisode, 'originalAirDate');
  if (originalAirDate) {
    subtitle = formatAiredDate(originalAirDate, 'Aired ', 'MM/DD/YYYY');
  }
  return {
    badge,
    id,
    image,
    link: helperGetRelativeUrl({ url: screenUrl }),
    name,
    networkIcon,
    originalItem: item,
    playButton: false,
    subtitle,
    title: name,
    titleLines: 1,
    titleLink: helperGetRelativeUrl({ url: screenUrl }),
    titleText: name,
    recommendationIdData,
  };
};

export const listProcessor = ({ item, isAuthenticated }) => {
  if (!item) return null;
  const {
    '@id': id,
    '@type': type,
    id: videoId,
    badge,
    callSign,
    detailUrl,
    fullEpisodeCount,
    images,
    name,
    playerScreenUrl,
    screenUrl,
    seriesName,
    seriesScreenUrl,
    theme,
    videoType,
  } = item;
  const { seriesRedirectMap } = window?.ExternalConfig || {};
  const isTubi = item?.contentSKU?.[0]?.includes('tubi');
  const requiresAuth = getRequiresAuth({ video: item });
  const displayBrand = getDisplayBrand(item);
  let titleLines = 1;
  const titleValue = seriesName || name;
  let title = isMovie(videoType)
    ? titleElement({ title: titleValue, requiresAuth, theme, isAuthenticated })
    : titleValue;

  if (fullEpisodeCount) {
    title = name;
    titleLines = 0; // set it to 0 so Tile skips sending it to React-dotdotdot.
  }
  const recommendationIdData = getRecommendationIdDataForItem(item);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const networkIcon = getNetworkLogoUrl(displayBrand);

  const callSignMap = window?.ExternalConfig?.callSignRedirects || {};
  let linkOverride = null;

  if (item?.seriesType === 'series' && seriesRedirectMap?.[item?.showCode]) {
    linkOverride = seriesRedirectMap?.[item?.showCode];
  }

  if (item.deeplinkWeb) {
    linkOverride = item.deeplinkWeb;
  }

  const url = screenUrl || seriesScreenUrl || playerScreenUrl || detailUrl;
  let videoData = {
    badge,
    id,
    image,
    link: linkOverride || helperGetRelativeUrl({ url }),
    name,
    networkIcon,
    originalItem: item,
    playButton: false,
    title,
    titleLines,
    titleLink: linkOverride || helperGetRelativeUrl({ url }),
    titleText: name,
    seriesScreenUrl: url,
    recommendationIdData,
  };

  if (callSignMap[callSign] && isLive(type)) {
    linkOverride = callSignMap[callSign];
  }

  if (isTubi) {
    videoData.image = item?.images?.landscape;
    videoData.link = item?.deeplink_url;
    videoData.isTubi = true;
  }

  if (isLive(type)) {
    const liveUrl = helperGetLiveUrl({ callSign, videoId });
    const requiresAuthLive = getRequiresAuth({ video: item, isLive: true });
    videoData.link = linkOverride || liveUrl;
    videoData.titleLink = linkOverride || liveUrl;
    videoData = externalRedirectProcessing(videoData);
    videoData.badge = SHOW_STATE_LIVE;
    videoData.title = titleElement({
      title: titleValue,
      requiresAuth: requiresAuthLive,
      theme,
      isAuthenticated,
    });

    // Fade tile only if it has a vod asset
    if (playerScreenUrl) videoData.fadeTile = isTileFaded(item, displayBrand);
  }

  return videoData;
};

export const listProcessorWithAnimation = ({ item, id, isAuthenticated }) => {
  if (!item) return null;
  const {
    global_moreInfoButton: moreInfoText,
    seriesDetail_latestEpisodeHeader: latestEpText,
  } = window.Language;
  const {
    '@type': type,
    latestFullEpisodeId,
    playerScreenUrl,
    requiresAuth,
    seriesScreenUrl,
  } = item;

  const isUpcoming = isUpcomingContent(item);
  const videoData = listProcessor({ item, isAuthenticated });
  videoData.isDisabled = false;
  if (isUpcoming) {
    videoData.badge = 'UPCOMING';
    const subTitle = getHomeSubtitleText(item);
    videoData.subtitle = subTitle;
    videoData.isDisabled = true;
  }
  let delay = Math.max(0, id);
  delay *= 0.1;
  if (delay <= 0) {
    videoData.inlineStyle = { opacity: 1, animation: 'none' };
  } else {
    videoData.inlineStyle = { animationDelay: `${delay}s` };
  }

  const ctaData = [];
  const showCTAs = optimizelyShowsTileCTAs();

  if (showCTAs && type === 'Series' && latestFullEpisodeId) {
    const latestEpisodeLink = helperGetVideoUrl(latestFullEpisodeId);
    const moreInfoLink = helperGetRelativeUrl({ url: playerScreenUrl || seriesScreenUrl });
    const showLock = requiresAuth && !isAuthenticated;

    const latestEpisodeText = optimizelyModifyShowsTileCTA() || `Watch ${latestEpText}`;

    ctaData.push(
      {
        icon: showLock ? 'lock' : 'play-button',
        text: latestEpisodeText,
        url: latestEpisodeLink,
        viewBox: showLock ? '-7 -1 30 30' : null,
      },
      {
        text: moreInfoText,
        url: moreInfoLink,
      },
    );
  }
  videoData.ctaData = ctaData;
  return videoData;
};

export const listItemProcessor = ({ item }) => {
  if (!item) return null;

  const { seriesRedirectMap } = window?.ExternalConfig || {};

  const {
    '@id': id,
    callSign,
    detailUrl,
    fullEpisodeCount,
    id: videoId,
    images,
    name,
    playerScreenUrl,
    screenUrl,
    seriesName,
    seriesScreenUrl,
  } = item;
  let title = seriesName || name;
  const displayBrand = getDisplayBrand(item);
  const isTubi = item?.contentSKU?.[0]?.includes('tubi');

  let linkOverride = null;

  if (item?.seriesType === 'series' && seriesRedirectMap?.[item?.showCode]) {
    linkOverride = seriesRedirectMap?.[item?.showCode];
  }

  if (item?.deeplinkWeb) {
    linkOverride = item.deeplinkWeb;
  }

  if (fullEpisodeCount) {
    title = name;
  }
  const url = linkOverride || screenUrl || seriesScreenUrl || playerScreenUrl || detailUrl;
  const networkLogoUrl = getNetworkLogoUrl(displayBrand, 25);
  const recommendationIdData = getRecommendationIdDataForItem(item);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });

  let videoData = {
    iconUrl: networkLogoUrl,
    id,
    image,
    name,
    networkIcon: networkLogoUrl,
    recommendationIdData,
    seriesScreenUrl,
    title,
    titleLink: linkOverride || helperGetRelativeUrl({ url }),
    titleText: name,
  };

  if (videoIsLive(item)) {
    const isUpcoming = isUpcomingContent(item);
    videoData.isDisabled = false;
    if (isUpcoming) {
      videoData.badge = 'UPCOMING';
      const subTitle = getHomeSubtitleText(item);
      videoData.subtitle = subTitle;
      videoData.isDisabled = true;
    } else {
      videoData.badge = SHOW_STATE_LIVE;
      videoData.isLive = true;
      const liveUrl = helperGetLiveUrl({ callSign, videoId });
      videoData.link = liveUrl;
      videoData.titleLink = liveUrl;
      videoData = externalRedirectProcessing(videoData);
    }
  }

  if (isTubi) {
    videoData.image = item?.images?.landscape;
    videoData.link = item?.deeplink_url;
    videoData.isTubi = true;
  }

  return videoData;
};

export const scheduleItemVideoProcessor = ({ item }) => {
  if (!item) {
    return null;
  }
  const { '@id': id, images } = item;
  const displayBrand = getDisplayBrand(item);

  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const networkIcon = getNetworkLogoUrl(displayBrand);

  const videoData = {
    id,
    image,
    playButton: false,
    networkIcon,
  };
  return videoData;
};

export const seriesCollectionVideoProcessor = ({ item }) => {
  if (!item) return null;

  const data = seriesVideoProcessor({ item });
  const {
    theme,
    seriesName: title,
    name: subtitle,
    description,
    originalAirDate,
    available,
    datePublished,
    contentRating,
    subRatings,
    durationInSeconds,
  } = item;

  const requiresAuth = getRequiresAuth({ video: item });
  const airDate = formatAiredDate(originalAirDate || datePublished || available, '', 'MM-DD-YY');
  const duration = formatDurationInSeconds(durationInSeconds);
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;

  const metadata = [];
  if (airDate) metadata.push(airDate);
  if (ratingStr) metadata.push(ratingStr);
  if (duration) metadata.push(duration);

  data.title = titleElement({ title, requiresAuth, theme });
  data.subtitle = subtitle;
  data.description = description;
  data.metadata = formatMetadata(metadata);
  return data;
};

export const endcardVideoProcessor = ({ item, account, isAuthenticated }) => {
  if (!item) {
    return null;
  }

  const {
    '@id': id,
    id: videoId,
    images,
    name,
    seriesName,
    playerScreenUrl,
    seriesScreenUrl,
    livePlayerScreenUrl,
    originalAirDate,
    contentRating,
    subRatings,
    displaySeasonAndEpisodeCounts,
    episodeNumber,
    seasonNumber,
    videoType,
    releaseYear,
    durationInSeconds,
    percentWatched,
    counter,
  } = item;

  const displayBrand = getDisplayBrand(item);

  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const badgeComponent = getItemBadge(item);
  const airDate = formatAiredDate(originalAirDate);
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;
  const duration = formatDurationInSeconds(durationInSeconds);
  let metadata = formatMetadata([airDate, ratingStr, duration]);
  const seasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const requiresAuth = getRequiresAuth({ video: item });
  const lockElement = requiresAuthElement({
    requiresAuth,
    isAuthenticated,
    viewBox: '0 4 24 24',
  });

  let subtitle;
  if (isMovie(videoType)) {
    metadata = formatMovieMetadata({
      releaseYear,
      contentRating,
      subRatings,
      durationInSeconds,
    });
  }
  if (isEpisode(videoType) || isClip(videoType)) {
    subtitle = `${displaySeasonAndEpisodeCounts ? seasonEpisode : ''}${name}`;
  }

  const title = seriesName || name;
  const titleComponent = (
    <div>
      <span>{counter}</span>
      <span>
        {title}
        {lockElement}
      </span>
    </div>
  );

  const linkHash = getPlaylistHash(playerScreenUrl) || CONTINUOUS;
  let link = helperGetRelativeUrl({ url: playerScreenUrl || seriesScreenUrl, hash: linkHash });
  if (livePlayerScreenUrl) link = helperGetLiveUrl({ videoId });
  const videoData = {
    id,
    link,
    title: titleComponent,
    titleLink: link,
    titleText: title,
    subtitle: subtitleElement(subtitle),
    subtitleName: name,
    subtitleText: subtitle,
    image,
    playButton: true,
    badge: badgeComponent,
    description: null,
    descriptionLines: null,
    metadata,
    videoType,
    networkIcon,
    percentWatched,
    trackButtonClick: handleTealiumButtonAction,
    account,
    tileDetailToggle: false,
    isAuthenticated,
    originalItem: item,
  };

  return videoData;
};

export const collectionsClipsProcessor = ({ item }) => {
  if (!item) return null;
  const {
    '@id': id,
    '@type': type,
    images,
    isSeriesDetailAvailable,
    name,
    network,
    originalAirDate,
    percentWatched,
    playerScreenUrl,
  } = item;

  const displayBrand = getDisplayBrand(item);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const timeSinceAired = formatAiredDate(originalAirDate, '', FORMAT_DISTANCE);

  const videoData = {
    id,
    image,
    isSeries: isTypeSeries(type),
    isSeriesDetailAvailable,
    link: helperGetRelativeUrl({ url: playerScreenUrl }),
    network,
    percentWatched,
    timeSinceAired,
    title: name,
    titleLines: 2,
    titleLink: helperGetRelativeUrl({ url: playerScreenUrl }),
    trackButtonClick: handleTealiumButtonAction,
  };

  return videoData;
};

export const primeTimeVideoProcessor = ({ item }) => {
  if (!item) return null;
  const {
    '@id': id,
    images,
    seriesName,
    secondaryTitle,
    screenUrl,
    seriesScreenUrl,
    contentRating,
    subRatings,
    description,
    network,
    percentWatched,
    isSeriesDetailAvailable,
  } = item;

  const displayBrand = getDisplayBrand(item);
  const fadeTile = isTileFaded(item, displayBrand);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const badge = getPrimetimeScheduleItemBadge(item);
  const rating = helperFormatRating(contentRating);
  const subRating = formatSubRatings(subRatings);
  const ratingStr = rating || subRating ? `${rating} ${subRating}` : null;
  const metadata = ratingStr;
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const seasonNumFormat = item.seasonNumber ? `S${item.seasonNumber}` : '';
  const episodeNumFormat = item.episodeNumber ? ` E${item.episodeNumber}` : '';
  const seasonEpisodeNum = seasonNumFormat ? `${seasonNumFormat}${episodeNumFormat} ` : '';
  const title = seriesName;
  let subtitle = `${seasonEpisodeNum}${secondaryTitle || ''}`;
  if (title === subtitle) subtitle = null;
  const url = screenUrl || seriesScreenUrl;

  const titleLink = isSeriesDetailAvailable ? helperGetRelativeUrl({ url }) : null;
  const videoData = {
    id,
    title: seriesName,
    titleLink,
    subtitle,
    description,
    image,
    playButton: true,
    badge,
    metadata,
    networkIcon,
    percentWatched,
    fadeTile,
    network,
  };

  return videoData;
};

export const singleStationVideoProcessor = ({ item, isAuthenticated }) => {
  if (!item) return null;
  const {
    '@id': id,
    '@type': type,
    images,
    seriesName,
    secondaryTitle,
    description,
    network,
  } = item;
  const videoIsTypeLive = isLive(type);
  const requiresAuth = getRequiresAuth({ video: item });
  const isLocked = requiresAuth && !isAuthenticated;
  const badge = getItemBadge(item, videoIsTypeLive);
  const displayBrand = getDisplayBrand(item);
  const fadeTile = isTileFaded(item, displayBrand);
  const imageObj = getVideoObjectImages({ images });
  const image = getVideoObjectImage({ image: imageObj, network: displayBrand });
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const seasonNumFormat = item.seasonNumber ? `S${item.seasonNumber}` : '';
  const episodeNumFormat = item.episodeNumber ? ` E${item.episodeNumber}` : '';
  const seasonEpisodeNum = seasonNumFormat ? `${seasonNumFormat}${episodeNumFormat} ` : '';
  const title = seriesName;
  let subtitle = `${seasonEpisodeNum}${secondaryTitle || ''}`;
  if (title === subtitle) subtitle = null;

  const videoData = {
    badge,
    description,
    fadeTile,
    id,
    image,
    isLocked,
    network,
    networkIcon,
    subtitle,
    title: seriesName,
  };

  return videoData;
};

requiresAuthElement.defaultProps = {
  isAuthenticated: false,
  lockClass: null,
  requiresAuth: false,
  viewBox: null,
};

requiresAuthElement.propTypes = {
  isAuthenticated: PropTypes.bool,
  lockClass: PropTypes.string,
  requiresAuth: PropTypes.bool,
  viewBox: PropTypes.shape({}),
};

titleElement.defaultProps = {
  hideLockIcon: false,
  isAuthenticated: false,
  isHoverStateV2: false,
};

titleElement.propTypes = {
  hideLockIcon: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isHoverStateV2: PropTypes.bool,
  requiresAuth: PropTypes.bool.isRequired,
  theme: PropTypes.shape({}).isRequired,
  title: PropTypes.string.isRequired,
  video: PropTypes.bool,
};
